import { renderRateAsPercentage } from "../../utils/rates";

export interface DisclosureTextSegment {
  content: string;
  italic?: boolean;
}

export interface DisclosureLink {
  url: string;
  text: string;
}

export interface DisclosureSegmentGroup {
  segments: DisclosureTextSegment[];
  links?: DisclosureLink[];
}

export interface DisclosureConfig {
  groups: DisclosureSegmentGroup[];
}

export interface DisclosureFooterProps {
  disclosureType:
    | "default"
    | "defaultWithDebit"
    | "defaultWithDebitAndFDIC"
    | "defaultWithFDIC"
    | "apyDisclosure"
    | "fdicDisclosure"
    | "landingPage";
  apyRate?: number;
  interestRate?: number;
}

type SegmentBuilder = (props: { apyRate?: number; interestRate?: number }) => DisclosureTextSegment | null;

export const getDisclosureConfig = (
  disclosureType: DisclosureFooterProps["disclosureType"],
  apyRate?: number,
  interestRate?: number
): DisclosureConfig => {
  const props = { apyRate, interestRate };

  const baseSegment: SegmentBuilder = () => ({
    content:
      "Wayflyer is a financial technology company and is not an FDIC-insured bank. Banking services provided by Thread Bank, Member FDIC. FDIC deposit insurance covers the failure of an insured bank. Certain conditions must be satisfied for pass-through deposit insurance coverage to apply.",
  });

  const debitSegment: SegmentBuilder = () => ({
    content:
      "Wayflyer is a financial technology company and is not an FDIC-insured bank. Banking services provided by Thread Bank, Member FDIC. FDIC deposit insurance covers the failure of an insured bank. The Wayflyer Visa debit card is issued by Thread Bank, Member FDIC, pursuant to a license from Visa U.S.A. Inc. and may be used anywhere Visa cards are accepted. Certain conditions must be satisfied for pass-through deposit insurance coverage to apply.",
  });

  const apySegment: SegmentBuilder = ({ apyRate, interestRate }) => {
    if (!apyRate || !interestRate) {
      return null;
    }
    return {
      content: `The interest rate on your account is ${renderRateAsPercentage(
        interestRate
      )} with an annual percentage yield of ${renderRateAsPercentage(
        apyRate
      )}. Your interest rate and annual percentage yield are variable and subject to change based on the target range of the Federal Funds Rate. Fees may affect your earnings on the account.`,
    };
  };

  const fdicSegment: SegmentBuilder = () => ({
    content:
      "*Your deposits qualify for up to $3,000,000 in FDIC insurance coverage when placed at program banks in the Thread Bank deposit sweep program. Your deposits at each program bank become eligible for FDIC insurance up to $250,000, inclusive of any other deposits you may already hold at the bank in the same ownership capacity. Please contact Thread bank with questions on the sweep program. Pass-through insurance coverage is subject to conditions.",
  });

  const fdicLinks: DisclosureLink[] = [
    { url: "https://thread.bank/sweep-disclosure/", text: "Thread bank Terms and conditions" },
    { url: "https://thread.bank/program-banks/", text: "Program banks" },
    { url: "mailto:customerservice@thread.bank", text: "Contact Thread bank" },
  ];

  const buildSegments = (...builders: SegmentBuilder[]): DisclosureTextSegment[] => {
    return builders
      .map((builder) => builder(props))
      .filter((segment): segment is DisclosureTextSegment => segment !== null);
  };

  const configs: Record<DisclosureFooterProps["disclosureType"], DisclosureConfig> = {
    default: {
      groups: [
        {
          segments: buildSegments(baseSegment, apySegment),
        },
      ],
    },
    defaultWithDebit: {
      groups: [
        {
          segments: buildSegments(debitSegment, apySegment),
        },
      ],
    },
    defaultWithDebitAndFDIC: {
      groups: [
        {
          segments: buildSegments(debitSegment, apySegment),
        },
        {
          segments: buildSegments(fdicSegment),
          links: fdicLinks,
        },
      ],
    },
    defaultWithFDIC: {
      groups: [
        {
          segments: buildSegments(baseSegment),
        },
        {
          segments: buildSegments(fdicSegment),
          links: fdicLinks,
        },
      ],
    },
    apyDisclosure: {
      groups: [
        {
          segments: buildSegments(apySegment),
        },
      ],
    },
    fdicDisclosure: {
      groups: [
        {
          segments: buildSegments(baseSegment, fdicSegment),
          links: fdicLinks,
        },
      ],
    },
    landingPage: {
      groups: [
        {
          segments: buildSegments(debitSegment, apySegment),
        },
        {
          segments: buildSegments(fdicSegment),
          links: fdicLinks,
        },
      ],
    },
  };

  return configs[disclosureType];
};
